import React, { useState } from "react"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { usePageContext } from "vike-react/usePageContext"
import { twMerge } from "tailwind-merge"
import Logo from "../Logo"
import LanguageSwitcher from "../Navbar/LanguageSwitcher"
import AppsIcons from "../Storefront/ui/AppsIcons"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import PubSub from "pubsub-js"
import { CONSENT_OPEN } from "aldoo-ra/Tracking/ConsentBanner"
import useT from "../Storefront/hooks/useT"
import { LockClosedIcon } from "@heroicons/react/24/solid"
import PaymentLogos from "../Storefront/checkout/PaymentLogos"

/** The ways the store takes a payment, as the cart and the payment step show them (checkout/PaymentLogos). */
function PaymentMethods({ className = "" }) {
  const t = useT()
  return (
    <div className={twMerge("flex flex-col gap-2", className)} data-testid="footer-payments">
      <span className="flex items-center gap-1.5 text-sm text-text_inv/70">
        <LockClosedIcon className="w-4 h-4" />
        {t`Secure Payments by Stripe`}
      </span>
      {/* the store as a whole: online, and cash on delivery with the sellers who take it */}
      <PaymentLogos online cashOnDelivery />
    </div>
  )
}

const YOUTUBE = "https://www.youtube.com/@aldoobooks"
const FACEBOOK = "https://www.facebook.com/aldooltd"

function YouTubeIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M10 15l5.19-3L10 9v6m11.56-7.83c.13.47.22 1.1.28 1.9.07.8.1 1.49.1 2.09L22 12c0 2.19-.16 3.8-.44 4.83-.25.9-.83 1.48-1.73 1.73-.47.13-1.33.22-2.65.28-1.3.07-2.49.1-3.59.1L12 19c-4.19 0-6.8-.16-7.83-.44-.9-.25-1.48-.83-1.73-1.73-.13-.47-.22-1.1-.28-1.9-.07-.8-.1-1.49-.1-2.09L2 12c0-2.19.16-3.8.44-4.83.25-.9.83-1.48 1.73-1.73.47-.13 1.33-.22 2.65-.28 1.3-.07 2.49-.1 3.59-.1L12 5c4.19 0 6.8.16 7.83.44.9.25 1.48.83 1.73 1.73z" />
    </svg>
  )
}

function FacebookIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4v-7h2.5l.5-3h-3V9.5c0-.9.3-1.5 1.5-1.5H18V5.4c-.3 0-1.3-.1-2.5-.1-2.5 0-4 1.5-4 4V11H9v3h2.5v7H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
    </svg>
  )
}

function Social({ className = "" }) {
  return (
    <div className={twMerge("flex flex-row gap-1.5", className)}>
      <a href={YOUTUBE} target="_blank" rel="noopener noreferrer" aria-label="Aldoo on YouTube" className="flex h-11 w-11 items-center justify-center rounded-full text-text_inv hover:bg-paper/10">
        <YouTubeIcon className="w-9 h-9" />
      </a>
      <a href={FACEBOOK} target="_blank" rel="noopener noreferrer" aria-label="Aldoo on Facebook" className="flex h-11 w-11 items-center justify-center rounded-full text-text_inv hover:bg-paper/10">
        <FacebookIcon className="w-8 h-8" />
      </a>
    </div>
  )
}

function Copyright() {
  return (
    <Typography variant="txt1" className="flex items-center gap-1 text-text_inv/60">
      <span aria-hidden="true">©</span> Aldoo Ltd, {new Date().getFullYear()}
    </Typography>
  )
}

function useFooterSections() {
  const t = useT()
  return [
    {
      id: "contact-us",
      title: t`Contact Us`,
      links: [
        { label: t`Contacts`, to: "/contact" },
      ],
    },
    {
      id: "learn-more",
      title: t`Learn More`,
      links: [
        { label: t`Help Center`, to: "/help-center" },
        { label: t`Terms of Use`, to: "/terms-of-use" },
        { label: t`Returns Policy`, to: "/returns-policy" },
        { label: t`Refunds Policy`, to: "/refunds-policy" },
        { label: t`Privacy Policy`, to: "/privacy-policy" },
        // the cookie banner again, to change the choice made
        { label: t`Cookie settings`, onClick: () => PubSub.publish(CONSENT_OPEN) },
      ],
    },
    // the desktop footer shows it in its own last column
    {
      id: "merchants",
      title: t`For merchants`,
      links: [
        { label: t`Merchant Portal`, to: "/vendor" },
        { label: t`Merchant guide`, to: "/vendor/help-center" },
      ],
    },
  ]
}

function FooterLink({ link, className = "" }) {
  const cls = twMerge("text-base text-text_inv/70 hover:text-text_inv leading-tight transition-colors", className)
  if (link.onClick) {
    return (
      <button 
        type="button" 
        onClick={link.onClick} 
        className={twMerge(cls, "text-left p-0 bg-transparent border-0 cursor-pointer")}
      >
        {link.label}
      </button>
    )
  }
  if (link.href) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className={cls}>
        {link.label}
      </a>
    )
  }
  return (
    <Button variant="none" href={link.to} className={cls}>
      {link.label}
    </Button>
  )
}

function Offices({ className = "" }) {
  const t = useT()
  return (
    <div className={twMerge("grid grid-cols-1 sm:grid-cols-2 gap-x-10 text-base leading-[34px] text-text_inv/70", className)}>
      <div className="flex flex-col">
        <span className="text-text_inv mb-1">{t`United Kingdom`}</span>
        <span>
          Barttelot Court
          <br /> Barttelot Road
          <br /> Horsham
          <br /> West Sussex
          <br /> RH12 1DQ
        </span>
      </div>
      <div className="flex flex-col mt-8 sm:mt-0">
        <span className="text-text_inv mb-1">{t`Bulgaria`}</span>
        <span>
          {t`Srebarna 2`}
          <br />
          {t`Promishlena zona "Hladilnika"`}
          <br />
          {t`Sofia`}
          <br />
          1407
        </span>
      </div>
    </div>
  )
}

function FooterDesktop() {
  const t = useT()
  const sections = useFooterSections()
  const merchants = sections.find((section) => section.id === "merchants")
  return (
    <div className="hidden lg:flex w-full flex-col items-center gap-8 bg-dark py-8">
      {/* <AppsIcons /> */}
      {/* below xl the logo sits above the columns; from xl, beside them */}
      <div className="flex w-full flex-col xl:flex-row items-start justify-between gap-8 px-[7%] lg:px-[15%] xl:px-[19%] pt-8">
        <Logo />
        {/* below xl one grid of three equal columns: the two of the links, then the merchants' */}
        <div className="grid w-full grid-cols-3 gap-x-3 xl:contents">
          <div className="col-span-2 grid w-full xl:w-[45%] grid-cols-2 gap-x-3 gap-y-10">
            {sections.filter((section) => section !== merchants).map((section) => (
              <div key={section.id} className="flex flex-col gap-3">
                <Typography 
                  variant="h5" 
                  className="font-semibold text-text_inv mb-2"
                >
                  {section.title}
                </Typography>
                {section.links.map((link) => (
                  <FooterLink key={link.label} link={link} />
                ))}
              </div>
            ))}
            <div className="col-span-2">
              <Typography 
                variant="h5" 
                className="font-semibold text-text_inv"
              >
                {t`Offices`}
              </Typography>
            </div>
            <div className="col-span-3 -mt-6">
              <Offices />
            </div>
          </div>

          {/* the merchants' column, the payment marks at its foot (level with the offices' last line) */}
          <div className="flex flex-col items-start gap-3 self-stretch">
            <Typography 
              variant="h5" 
              className="font-semibold text-text_inv mb-2"
            >
              {merchants.title}
            </Typography>
            {merchants.links.map((link) => (
              <FooterLink key={link.label} link={link} />
            ))}

            {/* <Social className="mt-10" /> */}
            <PaymentMethods className="mt-auto" />
          </div>
        </div>
      </div>
      <hr className="w-full border-white/10 m-0" />
      <Copyright />
    </div>
  )
}

function FooterAccordion() {
  const t = useT()
  const sections = useFooterSections()
  const [openId, setOpenId] = useState(null)
  const toggle = (id) => setOpenId((current) => (current === id ? null : id))
  // old accordion order: Contact Us, Learn More, Offices
  const order = ["contact-us", "learn-more", "merchants"]
  const rows = [...[...sections].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id)), { id: "offices", title: t`Offices`, content: <Offices /> }]

  return (
    <div className="flex lg:hidden w-full flex-col items-center bg-dark px-4 pt-8">
      <div className="mt-0 mb-8">
        <Logo />
      </div>
      
      <hr className="w-full border-white/10 m-0" />
      {rows.map((row) => {
        const open = openId === row.id
        return (
          <div key={row.id} className="w-full border-b border-white/10">
            <button
              type="button"
              onClick={() => toggle(row.id)}
              aria-expanded={open}
              aria-controls={`footer-${row.id}`}
              className="flex w-full items-center justify-between py-4 text-left"
            >
              <span className="text-lg font-semibold text-text_inv">
                {row.title}
              </span>
              <ChevronDownIcon className={twMerge("w-6 h-6 text-text_inv transition-transform", open && "rotate-180")} />
            </button>
            {open && (
              <div id={`footer-${row.id}`} className="flex flex-col gap-[30px] pb-6">
                {row.content ?? row.links.map((link) => <FooterLink key={link.label} link={link} />)}
              </div>
            )}
          </div>
        )
      })}
      {/* <Social className="my-5" /> */}
      <PaymentMethods className="items-center py-6" />
      <hr className="w-full border-white/10 m-0" />
      <div className="mt-6 pb-24">
        <Copyright />
      </div>
    </div>
  )
}

// aldoo.com footer: columns on desktop, accordion on mobile
function Footer() {
  const pageContext = usePageContext()
  const currentPath = pageContext?.currentPath || pageContext?.urlPathname || "/"
  if (/^\/([a-z]{2}\/)?admin(\/|$)/.test(currentPath)) return null

  return (
    <footer id="footer" className="w-full mt-auto">
      <FooterAccordion />
      <FooterDesktop />
    </footer>
  )
}

// AldooComponent definition for auto-discovery
export const AldooComponent = {
  id: "Footer",
  component: Footer,
  description: "aldoo.com footer: app store badges, link columns (accordion on mobile), offices, language selector, social links. Auto-hides on admin routes.",
  input: {},
}

export default Footer
